export type ApiErrorCategory = string;

export type ApiErrorShape = {
    code: string;
    message: string;
    hint?: string;
    category?: ApiErrorCategory;
    retryable?: boolean;
    correlationId?: string;
    requestId?: string;
    operationId?: string;
    details?: unknown;
};

export type ApiErrorPayload = {
    error: ApiErrorShape;
};

type ApiErrorDescriptor = ApiErrorShape;

type CreateApiErrorOptions = {
    status: number;
    descriptor: ApiErrorDescriptor;
    cause?: unknown;
};

const DEFAULT_API_ERROR_CODE = 'HttpRequestFailedError';

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
    return Object.prototype.toString.call(value) === '[object Object]';
};

const readOptionalString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const normalized = value.trim();
    return normalized ? normalized : undefined;
};

const readOptionalBoolean = (value: unknown): boolean | undefined => {
    return typeof value === 'boolean' ? value : undefined;
};

const toApiErrorDescriptor = (value: unknown): ApiErrorDescriptor | null => {
    if (!isObjectRecord(value)) {
        return null;
    }

    const message = readOptionalString(value.message);
    if (!message) {
        return null;
    }

    return {
        code: readOptionalString(value.code) ?? DEFAULT_API_ERROR_CODE,
        message,
        hint: readOptionalString(value.hint),
        category: readOptionalString(value.category),
        retryable: readOptionalBoolean(value.retryable),
        correlationId: readOptionalString(value.correlationId),
        requestId: readOptionalString(value.requestId),
        operationId: readOptionalString(value.operationId),
        ...(Object.prototype.hasOwnProperty.call(value, 'details') ? { details: value.details } : {}),
    };
};

export const extractApiErrorDescriptor = (payload: unknown): ApiErrorDescriptor | null => {
    if (typeof payload === 'string') {
        const message = readOptionalString(payload);
        return message
            ? {
                  code: DEFAULT_API_ERROR_CODE,
                  message,
              }
            : null;
    }

    if (!isObjectRecord(payload)) {
        return null;
    }

    if (typeof payload.error === 'string') {
        const message = readOptionalString(payload.error);
        return message
            ? {
                  code: DEFAULT_API_ERROR_CODE,
                  message,
              }
            : null;
    }

    const nestedDescriptor = toApiErrorDescriptor(payload.error);
    if (nestedDescriptor) {
        return nestedDescriptor;
    }

    return toApiErrorDescriptor(payload);
};

export const toApiErrorPayload = (descriptor: ApiErrorShape): ApiErrorPayload => ({
    error: descriptor,
});

export const formatApiErrorMessage = (
    descriptor: Pick<ApiErrorDescriptor, 'message' | 'hint'> | null | undefined,
    fallbackMessage = 'Request failed',
): string => {
    const baseMessage = descriptor?.message?.trim() || fallbackMessage;
    const hint = descriptor?.hint?.trim();

    if (!hint || hint === baseMessage) {
        return baseMessage;
    }

    return `${baseMessage} ${hint}`;
};

export class ApiError extends Error {
    readonly status: number;
    readonly code: string;
    readonly hint: string | null;
    readonly category: string | null;
    readonly retryable: boolean | null;
    readonly correlationId: string | null;
    readonly requestId: string | null;
    readonly operationId: string | null;
    readonly details: unknown;
    readonly payload: ApiErrorPayload;

    constructor({ status, descriptor, cause }: CreateApiErrorOptions) {
        super(formatApiErrorMessage(descriptor, `Request failed (${status})`), {
            cause: cause instanceof Error ? cause : undefined,
        });
        this.name = 'ApiError';
        this.status = status;
        this.code = descriptor.code;
        this.hint = descriptor.hint ?? null;
        this.category = descriptor.category ?? null;
        this.retryable = descriptor.retryable ?? null;
        this.correlationId = descriptor.correlationId ?? null;
        this.requestId = descriptor.requestId ?? null;
        this.operationId = descriptor.operationId ?? null;
        this.details = descriptor.details;
        this.payload = toApiErrorPayload(descriptor);
    }
}

export const createApiErrorFromResponse = ({
    status,
    payload,
    fallbackMessage,
    requestId,
}: {
    status: number;
    payload: unknown;
    fallbackMessage: string;
    requestId?: string | null;
}): ApiError => {
    const descriptor = extractApiErrorDescriptor(payload) ?? {
        code: DEFAULT_API_ERROR_CODE,
        message: fallbackMessage,
    };

    return new ApiError({
        status,
        descriptor: {
            ...descriptor,
            requestId: descriptor.requestId ?? requestId ?? undefined,
            correlationId: descriptor.correlationId ?? requestId ?? undefined,
        },
    });
};

export const extractApiErrorMessage = (payload: unknown, fallbackMessage?: string): string | null => {
    const descriptor = extractApiErrorDescriptor(payload);
    if (descriptor) {
        return formatApiErrorMessage(descriptor, fallbackMessage ?? descriptor.message);
    }

    if (fallbackMessage) {
        return fallbackMessage;
    }

    return null;
};

const formatApiValidationErrorDetails = (details: unknown): string | null => {
    if (!isObjectRecord(details) || !isObjectRecord(details.validationErrors)) {
        return null;
    }

    const messages = Object.values(details.validationErrors).flatMap((fieldErrors) => {
        if (!Array.isArray(fieldErrors)) {
            return [];
        }

        return fieldErrors
            .filter((message): message is string => typeof message === 'string')
            .map((message) => message.trim())
            .filter(Boolean);
    });

    return messages.length > 0 ? messages.join(' ') : null;
};

export const extractApiValidationErrorMessage = (payload: unknown): string | null => {
    const descriptor = extractApiErrorDescriptor(payload);
    return formatApiValidationErrorDetails(descriptor?.details);
};

export const extractApiValidationErrorMessageFromUnknown = (error: unknown): string | null => {
    if (error instanceof ApiError) {
        return formatApiValidationErrorDetails(error.details);
    }

    if (!error || typeof error !== 'object') {
        return null;
    }

    const responseData = (error as { response?: { data?: unknown } }).response?.data;
    if (responseData !== undefined) {
        return extractApiValidationErrorMessage(responseData);
    }

    return extractApiValidationErrorMessage(error);
};

export const extractApiErrorMessageFromUnknown = (error: unknown): string | null => {
    if (error instanceof ApiError) {
        return error.message;
    }

    if (!error || typeof error !== 'object') {
        return null;
    }

    const responseData = (error as { response?: { data?: unknown } }).response?.data;
    if (responseData !== undefined) {
        return extractApiErrorMessage(responseData);
    }

    return extractApiErrorMessage(error);
};
